import { useBlockNoteEditor } from '@blocknote/react';
import { useCallback, useEffect, useState } from 'react';

import { Dialog, DialogButton } from '@/components/ui/Dialog.web';
import { fetchBlock } from '@/lib/blocks/api';
import { searchLinkableDatabases, type LinkablePage } from '@/lib/blocks/page-link-search';
import { readViewConfigFromBlock, serializeViewConfig } from '@/lib/databases/view-config';
import { setActiveViewId } from '@couplespace/core/databases';

import { useEditorDatabaseViewPicker } from './editor-database-view-picker-context.web';
import { useEditorPageId } from './editor-page-context.web';
import { insertDatabaseEmbedAtCursor } from './insert-database-from-slash.web';

const databaseListIcon = (
  <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
    <rect height="7" rx="1" width="18" x="3" y="3" />
    <rect height="7" rx="1" width="18" x="3" y="14" />
    <path d="M3 10h18M8 3v7M16 3v7M8 14v7M16 14v7" />
  </svg>
);

export function DatabaseViewPickerHost() {
  const editor = useBlockNoteEditor();
  const pageId = useEditorPageId();
  const { registerOpen } = useEditorDatabaseViewPicker();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LinkablePage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewOptions, setViewOptions] = useState<{ id: string; name: string; type: string }[]>([]);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [viewsLoading, setViewsLoading] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setSelectedId(null);
    setViewOptions([]);
    setSelectedViewId(null);
  }, []);

  const openPicker = useCallback(() => {
    setOpen(true);
  }, []);

  useEffect(() => registerOpen(openPicker), [openPicker, registerOpen]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    void searchLinkableDatabases(query, { excludeId: pageId }).then((pages) => {
      if (cancelled) {
        return;
      }
      setResults(pages);
      setLoading(false);
      setSelectedId((current) => {
        if (current && pages.some((page) => page.id === current)) {
          return current;
        }
        return pages[0]?.id ?? null;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open, query, pageId]);

  useEffect(() => {
    if (!open || !selectedId) {
      setViewOptions([]);
      setSelectedViewId(null);
      return;
    }
    let cancelled = false;
    setViewsLoading(true);
    void fetchBlock(selectedId).then((block) => {
      if (cancelled) {
        return;
      }
      if (block.type !== 'database') {
        setViewOptions([]);
        setSelectedViewId(null);
        setViewsLoading(false);
        return;
      }
      const config = readViewConfigFromBlock(block);
      const views = config.views.map((v) => ({
        id: v.id,
        name: v.name,
        type: v.type,
      }));
      setViewOptions(views);
      setSelectedViewId(config.activeViewId);
      setViewsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, selectedId]);

  async function resolveViewConfigJson(): Promise<string> {
    if (!selectedId || !selectedViewId) {
      return '';
    }
    const block = await fetchBlock(selectedId);
    if (block.type !== 'database') {
      return '';
    }
    const config = readViewConfigFromBlock(block);
    const seeded = setActiveViewId(config, selectedViewId);
    return serializeViewConfig(seeded);
  }

  function onInsert() {
    if (!selectedId) {
      return;
    }
    editor.focus();
    void resolveViewConfigJson().then((viewConfigJson) => {
      insertDatabaseEmbedAtCursor(editor, selectedId, { viewConfigJson });
      close();
    });
  }

  return (
    <Dialog
      open={open}
      title="Database view"
      onClose={close}
      footer={
        <>
          <DialogButton label="Cancel" onPress={close} />
          <DialogButton
            label="Insert"
            variant="primary"
            disabled={!selectedId || viewsLoading}
            onPress={onInsert}
          />
        </>
      }
    >
      <div className="couplespace-db-view-picker">
        <label className="couplespace-db-view-picker-field">
          <span className="couplespace-db-view-picker-label">Database</span>
          <input
            className="couplespace-db-view-picker-input"
            type="search"
            placeholder="Search databases…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
          />
        </label>
        <div className="couplespace-link-picker-section">
          {loading ? (
            <p className="couplespace-db-view-picker-hint">Loading…</p>
          ) : results.length === 0 ? (
            <p className="couplespace-db-view-picker-hint">
              No databases found. Create one with / Database.
            </p>
          ) : (
            <ul className="couplespace-link-picker-list">
              {results.map((page) => (
                <li key={page.id}>
                  <button
                    type="button"
                    className={
                      selectedId === page.id
                        ? 'couplespace-link-picker-item couplespace-link-picker-item-selected'
                        : 'couplespace-link-picker-item'
                    }
                    onClick={() => setSelectedId(page.id)}
                  >
                    <span className="couplespace-link-picker-item-icon">{databaseListIcon}</span>
                    <span className="couplespace-link-picker-item-body">
                      <span className="couplespace-link-picker-item-title">{page.title}</span>
                      {page.breadcrumb ? (
                        <span className="couplespace-link-picker-item-breadcrumb">
                          {page.breadcrumb}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {selectedId ? (
          <label className="couplespace-db-view-picker-field">
            <span className="couplespace-db-view-picker-label">Start from view</span>
            {viewsLoading ? (
              <p className="couplespace-db-view-picker-hint">Loading views…</p>
            ) : viewOptions.length === 0 ? (
              <p className="couplespace-db-view-picker-hint">No saved views on this database.</p>
            ) : (
              <select
                className="couplespace-db-view-picker-select"
                value={selectedViewId ?? ''}
                onChange={(event) => setSelectedViewId(event.target.value || null)}
              >
                {viewOptions.map((view) => (
                  <option key={view.id} value={view.id}>
                    {view.name} ({view.type})
                  </option>
                ))}
              </select>
            )}
            <p className="couplespace-db-view-picker-hint">
              The embed keeps its own copy of views; changes here won&apos;t affect the source
              database.
            </p>
          </label>
        ) : null}
      </div>
    </Dialog>
  );
}
